import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, ChevronDown, Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { NAV, CONTACT, BRAND } from "../../data/site";

const Dropdown = ({ label, items, id }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        data-testid={`nav-${id}-trigger`}
        className="flex items-center gap-1.5 font-mono-x text-[11px] tracking-[0.1em] uppercase text-white/80 hover:text-white py-6 transition-colors"
      >
        {label} <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25 }}
            className="absolute left-1/2 -translate-x-1/2 top-full w-[420px] max-h-[70vh] overflow-y-auto bg-[#31465A] border border-white/15 shadow-2xl p-3 z-50"
          >
            {items.map((it) => (
              <Link
                key={it.to + it.label}
                to={it.to}
                data-testid={`nav-link-${id}${it.to.replaceAll("/", "-")}`}
                className="group flex flex-col px-5 py-3 hover:bg-[#3C5268] transition-colors"
              >
                <span className="font-display text-[15px] font-medium text-white group-hover:translate-x-1 transition-transform">
                  {it.label}
                </span>
                <span className="text-xs text-white/45 mt-0.5">{it.desc}</span>
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 bg-[#31465A] border-b border-black/25 shadow-lg"
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="py-2.5 shrink-0">
          <img
            src={BRAND.logo}
            alt="Cedar Views — Architectural Glazing"
            className="h-14 lg:h-[4.25rem] w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center justify-evenly flex-1 px-4">
          <Link to="/showroom/" data-testid="nav-showroom" className="font-mono-x text-[11px] tracking-[0.1em] uppercase text-white/85 hover:text-white py-6 transition-colors">
            Showrooms
          </Link>
          <Dropdown label="Windows" items={NAV.windows} id="windows" />
          <Dropdown label="Doors" items={NAV.doors} id="doors" />
          <Dropdown label="Brands" items={NAV.brands} id="brands" />
          <Link to="/building-services/" data-testid="nav-building" className="font-mono-x text-[11px] tracking-[0.1em] uppercase text-white/85 hover:text-white py-6 transition-colors">
            Building
          </Link>
          <Dropdown label="Professionals" items={NAV.professionals} id="professionals" />
          <Link to="/projects/" data-testid="nav-projects" className="font-mono-x text-[11px] tracking-[0.1em] uppercase text-white/85 hover:text-white py-6 transition-colors">
            Projects
          </Link>
          <Link to="/contact/" data-testid="nav-contact" className="font-mono-x text-[11px] tracking-[0.1em] uppercase text-white/85 hover:text-white py-6 transition-colors">
            Contact
          </Link>
        </nav>

        <div className="hidden lg:flex items-center gap-4 shrink-0">
          <a href={CONTACT.phoneHref} data-testid="nav-phone" className="hidden xl:flex items-center gap-2 whitespace-nowrap text-white/85 hover:text-white text-sm transition-colors">
            <Phone size={14} /> {CONTACT.phone}
          </a>
          <Link to="/showroom/" data-testid="nav-cta-quote" className="btn-book">
            {BRAND.bookLabel}
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <div className="hidden md:block bg-[#24333F] border-t border-white/5">
        <p className="text-center font-mono-x text-[9px] tracking-[0.3em] uppercase text-white/60 py-2">
          {BRAND.tagline}
        </p>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-[#31465A] border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 h-[calc(100dvh-5rem)] overflow-y-auto space-y-6">
              {[
                ["Windows", NAV.windows],
                ["Doors", NAV.doors],
                ["Brands", NAV.brands],
                ["Professionals", NAV.professionals],
              ].map(([group, items]) => (
                <div key={group}>
                  <p className="overline-x mb-3">{group}</p>
                  {items.map((it) => (
                    <Link key={it.to + it.label} to={it.to} className="block py-2 font-display text-lg text-white/90">
                      {it.label}
                    </Link>
                  ))}
                </div>
              ))}
              <div className="pt-2 border-t border-white/10 space-y-3">
                <Link to="/building-services/" className="block py-1 font-display text-lg text-white/90">Building</Link>
                <Link to="/projects/" className="block py-1 font-display text-lg text-white/90">Projects</Link>
                <Link to="/showroom/" className="block py-1 font-display text-lg text-white/90">Showrooms</Link>
                <Link to="/contact/" className="block py-1 font-display text-lg text-white/90">Contact</Link>
                <Link to="/showroom/" data-testid="mobile-cta-quote" className="btn-book mt-3">{BRAND.bookLabel}</Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
